import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import theme from '../../../theme';
import { ColorOptions } from '../../../theme/colors';
import { Dashboard } from '../../../types/files';

const useStyles = makeStyles({
  container: {
    borderLeft: '1px solid #e5e7eb',
    paddingLeft: 24,
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    minWidth: 0,
    position: 'relative',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      width: 0,
      height: 0,
      display: 'none',
    },
    '&::after': {
      content: '""',
      position: 'sticky',
      bottom: 0,
      left: 0,
      right: 0,
      display: 'block',
      height: 40,
      background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
      pointerEvents: 'none',
    },
  },
  headerLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  filterSelect: {
    fontSize: 13,
    color: '#374151',
    '& .MuiSelect-select': {
      padding: '4px 8px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e5e7eb',
    },
  },
  entryContainer: {
    display: 'flex',
    gap: 12,
    paddingBottom: 20,
    minWidth: 0,
    maxWidth: '100%',
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  entryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  entryType: {
    fontSize: 12,
    color: '#6b7280',
  },
  entryTimestamp: {
    fontSize: 11,
    color: '#9ca3af',
    whiteSpace: 'nowrap',
  },
  entryValue: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
    wordBreak: 'break-word',
    maxWidth: '34ch',
  },
  entryValueTruncated: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '34ch',
  },
  toggleButton: {
    fontSize: 12,
    color: '#3b82f6',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 26,
    padding: '0 10px',
    borderRadius: 24,
    fontFamily: 'Inter',
    fontSize: '0.8rem',
    fontWeight: 500,
    textTransform: 'capitalize',
  },
  empty: {
    fontSize: 13,
    color: '#9ca3af',
    fontStyle: 'italic',
  },
});

type TimelineEntrySource = 'status' | 'customField';

interface TimelineEntry {
  source: TimelineEntrySource;
  fieldName: string;
  value: string;
}

const STYLE_MAP: Record<TimelineEntrySource, { color: string; bgColor: string; icon: React.ElementType }> = {
  status: { color: '#3b82f6', bgColor: '#eff6ff', icon: SwapHorizIcon },
  customField: { color: '#10b981', bgColor: '#ecfdf5', icon: EditOutlinedIcon },
};

interface ActivityTimelineProps {
  dashboard: Dashboard;
}

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ dashboard }) => {
  const classes = useStyles();
  const { customFields, statuses } = useContext(GroupSettingsContext);
  const [filter, setFilter] = useState('all');
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const palettes = useMemo(() => Array.from(new Set(Object.values(ColorOptions)
    .filter(color => !['neutral', 'brown', 'lime', 'pink', 'red', 'shades'].includes(color)))), []);

  const getStatusColors = useCallback((title: string) => {
    const colorMap: Record<string, { bg: string; text: string; border: string }> = {
      'New':   { bg: '#F0FDFA', text: '#0D9488', border: '#99F6E4' },
      'Lead':  { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' },
      'Tier 1': { bg: '#FFF7ED', text: '#EA580C', border: '#FED7AA' },
      'Tier 2': { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' },
      'Tier 3': { bg: '#FEFCE8', text: '#CA8A04', border: '#FEF08A' },
      'Won':   { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0' },
      'Lost':  { bg: '#F9FAFB', text: '#4B5563', border: '#E5E7EB' },
    };
    const key = Object.keys(colorMap).find(k => k.toLowerCase() === title.toLowerCase());
    if (key) {
      const { bg, text, border } = colorMap[key];
      return { backgroundColor: bg, color: text, border: `1px solid ${border}` };
    }
    const index = statuses.findIndex(s => s.toLowerCase() === title.toLowerCase());
    if (index !== -1 && index < palettes.length) {
      return {
        backgroundColor: theme.colors[palettes[index]][50],
        color: theme.colors[palettes[index]][700],
        border: `1px solid ${theme.colors[palettes[index]][200]}`,
      };
    }
    return {
      backgroundColor: theme.colors.neutral[100],
      color: theme.colors.neutral[600],
      border: `1px solid ${theme.colors.neutral[500]}`,
    };
  }, [statuses, palettes]);

  const toggleExpand = (fieldName: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) next.delete(fieldName);
      else next.add(fieldName);
      return next;
    });
  };

  const lookUpCustomFields = (fieldName: string) => {
    const value = dashboard?.customFields?.find(({ name }) => name === fieldName)?.value;
    return Array.isArray(value) ? value : value ? [value] : [];
  };

  const entries = useMemo(() => {
    const items: TimelineEntry[] = [];

    // Status entry
    if (dashboard.status) {
      items.push({
        source: 'status',
        fieldName: 'Status',
        value: dashboard.status,
      });
    }

    // Custom text field entries
    customFields.forEach(({ name }) => {
      const values = lookUpCustomFields(name).filter(v => v.toLowerCase() !== 'none');
      if (values.length) {
        items.push({
          source: 'customField',
          fieldName: name,
          value: values.join(', '),
        });
      }
    });

    return items;
    // eslint-disable-next-line
  }, [dashboard, customFields]);

  const filterOptions = useMemo(() =>
    entries.map(entry => entry.fieldName),
    [entries]
  );

  const filtered = useMemo(() =>
    entries.filter(entry => filter === 'all' || entry.fieldName === filter),
    [entries, filter]
  );

  return (
    <Box className={classes.container}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
        <Typography className={classes.headerLabel}>Activity Timeline</Typography>
        <Select
          value={filter}
          onChange={(e) => setFilter(e.target.value as string)}
          size="small"
          className={classes.filterSelect}
        >
          <MenuItem value="all">All</MenuItem>
          {filterOptions.map(name => (
            <MenuItem key={name} value={name}>{name}</MenuItem>
          ))}
        </Select>
      </Stack>

      <Box className={classes.scrollArea}>
        <Stack spacing={0} sx={{ minWidth: 0, maxWidth: '100%' }}>
          {filtered.length > 0 ? filtered.map((entry, index) => {
            const style = STYLE_MAP[entry.source];
            const IconComponent = style.icon;
            return (
              <Box key={index} className={classes.entryContainer}>
                <Box className={classes.iconCircle} sx={{ backgroundColor: style.bgColor }}>
                  <IconComponent sx={{ fontSize: 18, color: style.color }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', width: 0 }}>
                  <Box className={classes.entryHeader}>
                    <Typography className={classes.entryType}>{entry.fieldName}</Typography>
                    <Typography className={classes.entryTimestamp}>
                      {moment(dashboard.updatedAt || dashboard.createdAt).fromNow()}
                    </Typography>
                  </Box>
                  {entry.source === 'status' ? (
                    <Typography className={classes.statusBadge} sx={getStatusColors(entry.value)}>
                      {entry.value}
                    </Typography>
                  ) : expandedFields.has(entry.fieldName) ? (
                    <>
                      <Typography className={classes.entryValue}>{entry.value}</Typography>
                      <button className={classes.toggleButton} onClick={() => toggleExpand(entry.fieldName)}>
                        Collapse
                      </button>
                    </>
                  ) : (
                    <>
                      <Typography className={classes.entryValueTruncated}>{entry.value}</Typography>
                      {entry.value.length > 40 && (
                        <button className={classes.toggleButton} onClick={() => toggleExpand(entry.fieldName)}>
                          Expand
                        </button>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            );
          }) : (
            <Typography className={classes.empty}>No activity yet</Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
